import * as React from "react";
import { SingleNavMenuLink, toLinkButton, toLinkDivider, toLinkPage, toLinkSpace } from "../identity/useNavMenu";
import { formSet, useCpFormAnyValueId, useCpFormModifiedAnyId } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useAppDispatch } from "../../store/hooks";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../envVariables";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { CpTagsNew } from "../../components/form/CpTagsNew";
import { linksOf } from "../../services/cpln";
import { formActions } from "../../store/formSlice";
import { AccessReportPage } from "../../components/detail/accessReport";
import { AuditTrail } from "../../components/detail/auditTrail";
import { Image } from "../../schema/types/image/entity";
import { Summary } from "./summary";
import { ImageDetailHeader } from "./header";
import { useLocation, useNavigate } from "react-router-dom";
import { CpRootError } from "../gvc/testNewForm/CpRootError";

type ImagePageState =
  | "-summary" //
  | "-tags"
  | "-audit-trail"
  | "-access-report";

const ImagePageStateValues: ImagePageState[] = ["-summary", "-tags", "-audit-trail", "-access-report"];

interface Props {
  image: Image;
  refetch?: () => void;
}
export const ImageDetailContent: React.FC<Props> = ({ image, refetch }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/image/${image.repository}:${image.tag}/*`);

  const { id: formId, submit } = useCpFormContext();

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [pageState, setPageState] = React.useState<ImagePageState>("-summary");

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);
  const anyModified = useCpFormModifiedAnyId(formId);

  React.useEffect(() => {
    const part = pathname.split(`/-`)[1];
    if (!ImagePageStateValues.includes(part as any)) {
      navigate(`${basePath}/${pageState}`);
    }
  }, []);

  React.useEffect(() => {
    const part = pathname.split(`/-`)[1];
    setPageState(`-${part}` as any);
  }, [pathname]);

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Summary", "/-summary"),
      toLinkPage("Tags", "/-tags", { names: ["tags"], showDirty: true }),
      toLinkDivider(),
      toLinkPage("Audit Trail", "/-audit-trail"),
      toLinkPage("Access Report", "/-access-report"),
    );

    if (anyModified) {
      links.push(
        toLinkSpace(),
        toLinkButton("Reset", {
          onClick: () => {
            dispatch(formActions.reset(formId));
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Preview", {
          onClick: () => {
            formSet(formId, `meta.previewing`, true);
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Update", {
          onClick: async () => {
            await submit();
          },
          type: "primary",
          className: "action",
          disabled: submitting,
          loading: submitting,
        }),
      );
    }

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      if (!anyModified) {
        links.push(toLinkSpace());
      }
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting,
        }),
      );
    }

    return links;
  }, [anyModified, submitting]);

  return (
    <BasePathContext.Provider value={basePath}>
      <ImageDetailHeader image={image} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            {pageState === "-summary" ? <Summary image={image} /> : null}
            {pageState === "-tags" ? <CpTagsNew /> : null}
            {pageState === "-audit-trail" ? (
              <AuditTrail kind={"image"} itemName={image.name} resourceId={image.id} version={image.version} selfLink={linksOf(image).self!} refetch={refetch} />
            ) : null}
            {pageState === "-access-report" ? <AccessReportPage itemLink={linksOf(image).self!} /> : null}
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
